import React, { useState } from 'react'
import { Users } from 'lucide-react'
import { apiDelete, apiGet, apiPatch, apiPost } from '../../api'
import { Button, EmptyState, initials, useConfirm } from '../../components/ui'
import { readError } from './shared'

function TeamsTab({ tournament, canManage, saving, run, setError }) {
  const confirm = useConfirm()
  const [newTeamName, setNewTeamName] = useState('')
  const [rename, setRename] = useState({})
  const [memberPhone, setMemberPhone] = useState({})
  const [phoneError, setPhoneError] = useState({})
  const teams = tournament.teams || []
  const isManual = tournament.teamSource === 'MANUAL'
  const isSquads = tournament.teamSource === 'SQUADS'

  const addTeam = (e) => {
    e.preventDefault()
    const name = newTeamName.trim()
    if (!name) return
    run(async () => {
      const res = await apiPost(`/api/tournaments/${tournament.id}/teams`, { name })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke legge til lag'))
      setNewTeamName('')
      return { tournament: await res.json() }
    }, 'Lag lagt til')
  }

  const renameTeam = (teamId) => {
    const name = (rename[teamId] || '').trim()
    if (!name) return
    run(async () => {
      const res = await apiPatch(`/api/tournaments/${tournament.id}/teams/${teamId}`, { name })
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke endre navn'))
      return { tournament: await res.json() }
    }, 'Lagnavn oppdatert')
  }

  const removeTeam = async (team) => {
    if (!(await confirm({ message: `Fjern laget «${team.name}»?`, confirmLabel: 'Fjern lag', danger: true }))) return
    run(async () => {
      const res = await apiDelete(`/api/tournaments/${tournament.id}/teams/${team.id}`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke fjerne lag'))
      return { tournament: await res.json() }
    }, 'Lag fjernet')
  }

  const addMemberByPhone = async (teamId) => {
    const phone = (memberPhone[teamId] || '').trim()
    if (!phone) return
    setError('')
    setPhoneError((prev) => ({ ...prev, [teamId]: null }))
    try {
      const lookup = await apiGet(`/api/users/lookup-by-phone?phone=${encodeURIComponent(phone)}`)
      const data = await lookup.json()
      if (!lookup.ok || !data.exists || !data.userId) {
        setPhoneError((prev) => ({ ...prev, [teamId]: 'Fant ingen bruker med det telefonnummeret.' }))
        return
      }
      await run(async () => {
        const res = await apiPost(`/api/tournaments/${tournament.id}/teams/${teamId}/members`, {
          userId: data.userId
        })
        if (!res.ok) throw new Error(await readError(res, 'Kunne ikke legge til spiller'))
        setMemberPhone((prev) => ({ ...prev, [teamId]: '' }))
        return { tournament: await res.json() }
      }, `${data.displayName || 'Spiller'} lagt til`)
    } catch (err) {
      setError(err.message || 'Kunne ikke legge til spiller')
    }
  }

  const removeMember = async (teamId, userId, name) => {
    if (!(await confirm({ message: `Fjern ${name}?`, confirmLabel: 'Fjern', danger: true }))) return
    run(async () => {
      const res = await apiDelete(`/api/tournaments/${tournament.id}/teams/${teamId}/members/${userId}`)
      if (!res.ok) throw new Error(await readError(res, 'Kunne ikke fjerne spiller'))
      return { tournament: await res.json() }
    }, 'Spiller fjernet')
  }

  const showAdd = canManage && isManual

  return (
    <div className="stack-lg">
      {(showAdd || isSquads) && (
        <div className="tn-two-col">
          {showAdd && (
            <section className="card tn-card" aria-labelledby="tn-add-h">
              <div className="stack-sm">
                <h2 id="tn-add-h" className="section-title">Legg til lag</h2>
                <span className="tn-footnote">For turneringer med egne lag</span>
              </div>
              <form className="tn-edit-fields" onSubmit={addTeam}>
                <label className="field tn-grow-3">
                  <span className="field-label">Lagnavn</span>
                  <input
                    placeholder="Lagnavn"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    disabled={saving}
                  />
                </label>
                <Button type="submit" className="tn-field-btn" disabled={saving || !newTeamName.trim()}>
                  Legg til
                </Button>
              </form>
            </section>
          )}
          {isSquads && (
            <div role="note" className="tn-squad-note">
              <span className="tn-squad-icon"><Users size={20} strokeWidth={1.9} aria-hidden="true" /></span>
              <p>
                Dette er en squad-turnering. Squads melder seg på via invitasjonskode
                {tournament.inviteCode ? <> (<b className="tn-code-inline">{tournament.inviteCode}</b>)</> : null}.
                Du kan fjerne påmeldte squads under.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="tn-head-row">
        <h2 className="section-title">Påmeldte lag</h2>
        <span className="tn-footnote">{teams.length} lag</span>
      </div>

      {teams.length === 0 ? (
        <EmptyState compact title="Ingen lag ennå." />
      ) : (
        <div className="tn-team-grid">
          {teams.map((team) => {
            const members = team.members || []
            const editable = canManage && isManual && !team.groupId
            const phoneErr = phoneError[team.id]
            return (
              <article key={team.id} className="card tn-card tn-team-card" aria-labelledby={`tn-team-${team.id}`}>
                <div className="tn-team-head">
                  <div className="stack-sm tn-min0">
                    <h3 id={`tn-team-${team.id}`} className="tn-team-title">
                      <span className="tn-team-seed">#{team.seed}</span> {team.name}
                    </h3>
                    <div className="row tn-team-sub">
                      {team.groupName && <span className="pill pill-live">Squad</span>}
                      <span className="tn-footnote">{members.length} spillere</span>
                    </div>
                  </div>
                  {canManage && (
                    <Button variant="danger-soft" size="sm" disabled={saving} onClick={() => removeTeam(team)}>
                      Fjern lag
                    </Button>
                  )}
                </div>

                {editable && (
                  <div className="tn-edit-fields">
                    <label className="field tn-grow-3">
                      <span className="field-label tn-small-label">Nytt lagnavn</span>
                      <input
                        placeholder="Nytt lagnavn"
                        value={rename[team.id] ?? team.name}
                        onChange={(e) => setRename({ ...rename, [team.id]: e.target.value })}
                        disabled={saving}
                      />
                    </label>
                    <Button variant="tertiary" size="sm" className="tn-field-btn sm" disabled={saving} onClick={() => renameTeam(team.id)}>
                      Endre navn
                    </Button>
                  </div>
                )}

                <div>
                  <span className="eyebrow tn-members-label">Spillere</span>
                  <ul className="tn-member-list">
                    {members.map((m) => (
                      <li key={m.userId}>
                        <span className="avatar avatar-mist tn-avatar-sm" aria-hidden="true">{initials(m.displayName)}</span>
                        <span className="tn-member-name">
                          {m.displayName}
                          {m.isSquadAdmin && <span className="muted"> · squad-admin</span>}
                        </span>
                        {editable && (
                          <Button
                            variant="danger-outline"
                            size="xs"
                            aria-label={`Fjern ${m.displayName}`}
                            disabled={saving}
                            onClick={() => removeMember(team.id, m.userId, m.displayName)}
                          >
                            Fjern
                          </Button>
                        )}
                      </li>
                    ))}
                    {members.length === 0 && <li className="tn-member-empty">Ingen spillere</li>}
                  </ul>
                </div>

                {editable && (
                  <form
                    className="tn-phone-box"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (!saving && (memberPhone[team.id] || '').trim()) addMemberByPhone(team.id)
                    }}
                  >
                    <div className="tn-edit-fields">
                      <label className="field tn-grow-3">
                        <span className="field-label tn-small-label">Telefonnr for å legge til spiller</span>
                        <input
                          type="tel"
                          inputMode="tel"
                          autoComplete="off"
                          placeholder="912 34 567"
                          aria-invalid={phoneErr ? true : undefined}
                          className={phoneErr ? 'tn-invalid' : undefined}
                          value={memberPhone[team.id] || ''}
                          onChange={(e) => setMemberPhone({ ...memberPhone, [team.id]: e.target.value })}
                          disabled={saving}
                        />
                      </label>
                      <Button
                        type="submit"
                        variant="secondary"
                        size="sm"
                        className="tn-field-btn sm"
                        disabled={saving || !(memberPhone[team.id] || '').trim()}
                      >
                        Legg til spiller
                      </Button>
                    </div>
                    {phoneErr && <span role="alert" className="field-error">{phoneErr}</span>}
                  </form>
                )}
              </article>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TeamsTab
